import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Button, Card, Container, Spinner } from "react-bootstrap";
import { Note } from "../../../../types/note";
import StandardView from "./StandardView";
import CreateNote from "./CreateNote";
import ContactView from "./ContactView";
import InterventionView from "./InterventionView";
import ReferralView from "./ReferralView";
import StudentConnectionsView from "./StudentConnectionsView";
import EditNote from "./EditNote";
import { fetchNotesLazy, fetchNoteTypes, fetchOrganization, fetchSummary, postSummary } from "../../../../services/httpClient";
import SelectObject from "../../../ui/SelectObject";
import { useParams } from "react-router-dom";
import ToastMsg from "../../../ui/ToastMsg";
import GroupMeetingView from "./GroupMeetingView";
import SaveChangesModal from "../../../ui/SaveChangesModal";
import useApp from "../../../../utils/useApp";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWandMagicSparkles } from "@fortawesome/free-solid-svg-icons";
import BanView from "./BanView";
import InfractionView from "./InfractionView";
import jsPDF from "jspdf";
import IntakeView from "./IntakeView";
import ExitView from "./ExitView";
import CustomNoteView from "./CustomNoteView";
import html2canvas from "html2canvas";
import SelectObjectTypeahead from "../../../ui/SelectObjectTypeahead";

const NOTE_LOAD_AMOUNT = 20;
const PAGE_SIZE = 100;

interface NotesState {
  createNote: boolean;
  setCreateNote: any;
  viewNotes: boolean;
  setViewNotes: any;
  editNote: boolean;
  setEditNote: any;
  myCase: boolean;
  setUserToSave: any;
  showModal: boolean;
  setShowModal: any;
  getData: boolean;
  family: any;
}

const Notes = ({
  createNote,
  setCreateNote,
  viewNotes,
  setViewNotes,
  editNote,
  setEditNote,
  myCase,
  setUserToSave,
  showModal,
  setShowModal,
  getData,
  family,
}: NotesState) => {
  const { id } = useParams();
  const { showMenu, auth }: any = useApp();
  const [type, setType] = useState<string>("View All");
  const [subject, setSubject] = useState<any>({
    value: "0",
    label: "View All",
    original: { key: "0", name: "View All" },
  });

  const updateSubject = (e: any) => {
    if (!e) {
      setSubject({
        value: "0",
        label: "View All",
        original: { key: "0", name: "View All" },
      });
      return;
    }

    const formatted = {
      ...e,
      value: e.id ?? e.key ?? e.value,
      label: e.name ?? e.label ?? "",
    };
    setSubject(formatted);
  };
  const [currentNote, setCurrentNote] = useState<Note>();
  const [currentNoteType, setCurrentNoteType] = useState<any>();
  const [notes, setNotes] = useState<any[]>([]);
  const [filteredNotes, setFilteredNotes] = useState<Note[]>([]);
  const [types, setTypes] = useState<any[]>([]);
  const [profileImage, setProfileImage] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastBody, setToastBody] = useState<string>("");
  const [toastText, setToastText] = useState<string>("");
  const [saveIsEnabled, setSaveIsEnabled] = useState<boolean>(false);
  const [summary, setSummary] = useState<string>("");
  const [isGettingSummary, setIsGettingSummary] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [canvas, setCanvas] = useState<any>();
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [notesLoaded, setNotesLoaded] = useState<number>(NOTE_LOAD_AMOUNT);
  const [reportType, setReportType] = useState<string>("");
  const [reportRunning, setReportRunning] = useState<boolean>(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsVisible((prev) => !prev);
    }, 1500);

    if (getData) {
      setIsLoading(true);
      getNotes();
      getNotesSummary();
      getOrg();
    }

    return () => clearInterval(interval);
  }, [getData]);

  useEffect(() => {
    if (reportRunning) {
      buildPDF(reportType);
      setReportRunning(false);
    }
  }, [canvas]);

  // Lazy paginated loading of notes
  const getNotes = () => {
    setIsLoading(true);
    fetchAllPages(1, []);
    getTypes();
  };

  // Fetch all pages of notes lazily
  const fetchAllPages = async (startPage: number, prevNotes: any[]) => {
    let allNotes = [...prevNotes];
    let loaded = new Set<number>();
    let fetching = new Set<number>();
    let isDone = false;

    const fetchPage = async (pageNum: number) => {
      if (loaded.has(pageNum) || fetching.has(pageNum) || isDone) return;
      fetching.add(pageNum);

      fetchNotesLazy(id, PAGE_SIZE, pageNum)
        .then((res: any) => {
          const meta = res[0]?.data.meta;
          const notes = res[0]?.data?.data || [];
          loaded.add(pageNum);
          fetching.delete(pageNum);

          // Merge new notes, avoid dupes by id
          allNotes = [...allNotes, ...notes.filter((n: any) => !allNotes.some((an) => an.id === n.id))];
          setNotes([...allNotes]);
          setFilteredNotes([...allNotes]);

          setIsLoading(false);
          // If there are more pages, fetch next
          if (meta && pageNum !== meta.last_page) {
            fetchPage(pageNum + 1);
          } else {
            isDone = true;
            setIsLoading(false);
          }
        })
        .catch((err: any) => {
          console.log(err);
          fetching.delete(pageNum);
          setIsLoading(false);
        });
    };

    fetchPage(startPage);
  };

  const getTypes = () => {
    fetchNoteTypes()
      .then((res: any) => {
        //gets all published and archived assessment types
        var types = res[0].data.filter((t: any) => t?.is_published === 1 || t?.default_is_published === 0);

        var sorted = [...types];
        sorted.sort((a: any, b: any) => a.is_archived - b.is_archived);

        setTypes(sorted);
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  //all family
  const familyMembers = useMemo(() => {
    const normalized = family.map((f: any) => ({
      id: f.id,
      type: f.role === "family" ? "guardian" : "member",
      key: `${f.firstname} ${f.lastname}`,
      name: `${f.firstname} ${f.lastname}`,
      isBanned: f.is_banned === 1 ? true : false,
      isDeleted: f.deleted_at ? true : false,
    }));

    return [...normalized].sort((a, b) => a.isDeleted - b.isDeleted);
  }, [family]);

  //non-deleted family
  const activeFamily = useMemo(() => {
    const filtered = familyMembers.filter((f: any) => !f.isDeleted);
    return [...filtered].sort((a, b) => a.isBanned - b.isBanned);
  }, [familyMembers]);

  const getOrg = () => {
    fetchOrganization(auth?.profile?.userOrgID)
      .then((res: any) => {
        const currentOrg = res[0].data;

        if (currentOrg?.profile.length > 0) {
          currentOrg?.profile?.map((p: any) => {
            const base64String = "data:image/jpeg;base64, " + p?.data;
            setProfileImage(base64String);
          });
        } else {
          setProfileImage("");
        }
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const cancelEditing = () => {
    if (saveIsEnabled) {
      setShowModal(true);
    } else {
      updateViewNotes(true);
    }
  };

  const updateViewNotes = (e: any) => {
    setViewNotes(e);
    setCreateNote(false);
    setEditNote(false);
    setUserToSave(false);
  };

  const updateCreateNotes = (e: any) => {
    setViewNotes(false);
    setCreateNote(e);
    setEditNote(false);
    setShowSummary(false);
    setSaveIsEnabled(false);
  };

  const updateEditNote = (e: any) => {
    setViewNotes(false);
    setCreateNote(false);
    setEditNote(e);
    setShowSummary(false);
  };

  useEffect(() => {
    var tempNotes = notes;
    setNotesLoaded(NOTE_LOAD_AMOUNT);

    const subjectKey = subject?.value || "0";
    const subjectName = subject?.label || "View All";
    const isSubjectViewAll = subjectKey === "0" || subjectName === "View All";
    const isTypeViewAll = type === "0" || type === "View All";

    if (subjectKey === "unassigned") {
      if (isTypeViewAll) {
        setFilteredNotes(tempNotes.filter((i: any) => !i.subject || !i.subject.name));
      } else {
        setFilteredNotes(tempNotes.filter((i: any) => i.notetype === type && (!i.subject || !i.subject.name)));
      }
      return;
    }

    if (!isTypeViewAll) {
      tempNotes = tempNotes.filter((i: any) => i.notetype === type);
      // trackDataEvent("filter", "note_type");
    }

    if (!isSubjectViewAll) {
      tempNotes = tempNotes.filter((i: any) => i.subject?.name === subjectName);
      // trackDataEvent("filter", "note_family_member");
    }

    setFilteredNotes(tempNotes);
  }, [type, subject, notes]);

  const getNotesSummary = async () => {
    fetchSummary(id)
      .then((res: any) => {
        var magic = res?.[0]?.data?.summary;
        setSummary(magic?.[0]?.notes ?? "");
      })
      .catch((err: any) => {
        console.log(err);
        setShowSummary(false);
        setIsGettingSummary(false);
      });
  };

  const createNotesSummary = async () => {
    postSummary(id)
      .then((res: any) => {
        var magic = res[0].data.summary?.postResponse;
        setSummary(magic);
        setShowSummary(true);
        setIsGettingSummary(false);
      })
      .catch((err: any) => {
        console.log(err);
        setShowSummary(false);
        setIsGettingSummary(false);
      });
  };

  const showNotesSummary = async () => {
    setShowSummary(false);
    setIsGettingSummary(true);
    if (summary?.length === 0 || summary === null) {
      await createNotesSummary();
    } else {
      setTimeout(() => {
        setIsGettingSummary(false);
        setShowSummary(true);
      }, 3000);
    }
  };

  const addMoreNotes = () => {
    setLoadingMore(true);
    setTimeout(() => {
      setNotesLoaded(notesLoaded + NOTE_LOAD_AMOUNT);
    }, 1);
    setTimeout(() => {
      setLoadingMore(false);
    }, 1);
  };

  const getImageDimensions = (base64: string): Promise<{ width: number }> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = base64;
      img.onload = () => {
        const aspectRatio = img.width / img.height;
        const targetWidth = 10 * aspectRatio;
        resolve({ width: targetWidth });
      };
    });
  };

  const buildPDF = async (type: string) => {
    try {
      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF("p", "mm", "a4");
      const margin = 10;
      const headerHeight = 20; // Adjust as needed
      const pageHeight = pdf.internal.pageSize.getHeight();
      const pdfWidth = pdf.internal.pageSize.getWidth() - 2 * margin;
      const imgProps = pdf.getImageProperties(imgData);
      const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;

      let yPosition = margin + headerHeight;

      // Add header to the first page
      if (profileImage) {
        const headerImg = new Image();
        headerImg.src = profileImage;
        const { width } = await getImageDimensions(profileImage);
        pdf.addImage(profileImage, "PNG", margin, margin, width, 10);
        pdf.setFontSize(12);
        pdf.text(type + " Report", margin + width + 5, margin + 7);
      } else {
        pdf.setFontSize(12);
        pdf.text(type + " Report", margin, margin + 7);
      }

      // Helper function to crop canvas
      const cropCanvas = (sourceCanvas: any, cropX: number, cropY: number, cropWidth: number, cropHeight: number) => {
        const tempCanvas = document.createElement("canvas");
        tempCanvas.width = cropWidth;
        tempCanvas.height = cropHeight;
        const ctx = tempCanvas.getContext("2d");
        ctx?.drawImage(sourceCanvas, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);
        return tempCanvas;
      };

      // Check if the content fits on one page
      if (imgHeight <= pageHeight - yPosition) {
        pdf.addImage(imgData, "PNG", margin, yPosition, pdfWidth, imgHeight);
      } else {
        // Handle multi-page case
        let remainingHeight = canvas.height;
        let position = 0;
        const pageCanvasHeight = 1800;

        while (remainingHeight > 0) {
          const pageCanvas = cropCanvas(canvas, 0, position, canvas.width, pageCanvasHeight);
          const pageImgData = pageCanvas.toDataURL("image/png");
          pdf.addImage(pageImgData, "PNG", margin, yPosition, pdfWidth, pageCanvasHeight * (pdfWidth / canvas.width));

          remainingHeight -= pageCanvasHeight;
          position += pageCanvasHeight;

          if (remainingHeight > 0) {
            pdf.addPage();
            yPosition = margin + headerHeight;
            if (profileImage) {
              const headerImg = new Image();
              headerImg.src = profileImage;
              const { width } = await getImageDimensions(profileImage);
              pdf.addImage(profileImage, "PNG", margin, margin, width, 10);
              pdf.setFontSize(12);
              pdf.text(type + " Report", margin + width + 5, margin + 7);
            } else {
              pdf.setFontSize(12);
              pdf.text(type + " Report", margin, margin + 7);
            }
          }
        }
      }

      const formattedDate = new Date()
        .toISOString()
        .replace(/[-:.TZ]/g, "")
        .slice(0, 14);

      //trigger download
      const blob = pdf.output("blob");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = type + "_" + formattedDate + ".pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };

  const downloadReport = async (e: any, n: any) => {
    e.stopPropagation();
    setReportRunning(true);

    await new Promise((resolve) => setTimeout(resolve, 1000));

    const reportSection = document.getElementById("note-card-" + n?.id);
    const downloadButton = document.getElementById("note-download-button-" + n?.id);
    const downloadTooltip = document.getElementById("note-download-tooltip-" + n?.id);
    const editButton = document.getElementById("note-edit-button-" + n?.id);
    const editTooltip = document.getElementById("note-edit-tooltip-" + n?.id);

    if (reportSection) {
      if (downloadButton) {
        downloadButton.style.visibility = "hidden";
        downloadButton.style.display = "none";
      }
      if (downloadTooltip) {
        downloadTooltip.style.visibility = "hidden";
        downloadTooltip.style.display = "none";
      }
      if (editButton) {
        editButton.style.visibility = "hidden";
        editButton.style.display = "none";
      }
      if (editTooltip) {
        editTooltip.style.visibility = "hidden";
        editTooltip.style.display = "none";
      }

      const images = reportSection.getElementsByTagName("img");
      for (let i = 0; i < images.length; i++) {
        images[i].crossOrigin = "anonymous";
      }

      const canvas = await html2canvas(reportSection, { useCORS: true });
      setCanvas(canvas);

      if (downloadButton) {
        downloadButton.style.visibility = "visible";
        downloadButton.style.display = "inline-block";
      }
      if (downloadTooltip) {
        downloadTooltip.style.visibility = "visible";
        downloadTooltip.style.display = "inline-block";
      }
      if (editButton) {
        editButton.style.visibility = "visible";
        editButton.style.display = "inline-block";
      }
      if (editTooltip) {
        editTooltip.style.visibility = "visible";
        editTooltip.style.display = "inline-block";
      }
    } else {
      console.error("Report section not found");
      setReportRunning(false);
    }
  };

  return (
    <>
      <ToastMsg showToast={showToast} setShowToast={setShowToast} toastText={toastText} toastBody={toastBody} />

      {isLoading ? (
        <Container>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : (
        <>
          {viewNotes === true && (
            <>
              <Row>
                <Col>
                  <h4 className="mb-0 app-header-text">Note History</h4>
                </Col>
                <Col className="text-end">
                  <Button className="app-tertiary-color button me-3" onClick={() => showNotesSummary()}>
                    <FontAwesomeIcon icon={faWandMagicSparkles} /> Recap
                  </Button>
                  <Button className="app-primary-color button" onClick={() => updateCreateNotes(true)}>
                    Create Note
                  </Button>
                </Col>
              </Row>
              {isGettingSummary && (
                <Row className="text-center">
                  <Col>
                    <h6 className={isVisible ? "app-orange-text fade-in-out" : " app-orange-text fade-in-out hidden"}>
                      Generating Recap <FontAwesomeIcon icon={faWandMagicSparkles} />
                    </h6>
                  </Col>
                </Row>
              )}
              {showSummary && (
                <>
                  <Row>
                    <h5 className="mb-0 app-header-text">Recap</h5>
                  </Row>
                  <Card className="mb-3">
                    <Row className="px-3 py-3">
                      <pre style={{ whiteSpace: "pre-wrap", wordWrap: "break-word", fontSize: "medium" }}>{summary}</pre>
                    </Row>
                    <div className="text-center text-muted mb-1 small-text">
                      {
                        "This content was generated using AI. While we strive for accuracy, we encourage users to verify important information."
                      }
                    </div>
                  </Card>
                </>
              )}
              <Card className={showMenu ? "bg-light sticky-top card-note-menu-open-sticky" : "bg-light sticky-top card-note-sticky"}>
                <Row className="pe-0 mb-3 mt-3 ms-1 me-1">
                  <Col className="col-6">
                    <SelectObject
                      initialOption="View All"
                      label="Note Type"
                      value={type}
                      options={types}
                      onUpdate={(e: any) => setType(e.target.value)}
                    />
                  </Col>
                  <Col className="col-6">
                    <SelectObjectTypeahead
                      initialOption="View All"
                      label="Household Member"
                      value={subject}
                      options={(() => {
                        const viewAll = { value: "0", label: "View All", original: { key: "0", name: "View All" } };
                        const unassigned = {
                          value: "unassigned",
                          label: "Unassigned",
                          original: { key: "unassigned", name: "Unassigned" },
                        };
                        const active = familyMembers
                          .filter((f: any) => !f.isDeleted && !f.isBanned)
                          .map((f: any) => ({ value: f.id, label: f.name, original: f }));
                        const banned = familyMembers
                          .filter((f: any) => f.isBanned)
                          .map((f: any) => ({ value: f.id, label: f.name, original: f }));
                        const deleted = familyMembers
                          .filter((f: any) => f.isDeleted)
                          .map((f: any) => ({ value: f.id, label: f.name, original: f }));
                        const groups: any[] = [];
                        if (active.length) groups.push({ label: "Active", options: active });
                        if (banned.length) groups.push({ label: "Banned", options: banned });
                        if (deleted.length) groups.push({ label: "Deleted", options: deleted });
                        return [viewAll, unassigned, ...groups];
                      })()}
                      groupHeading={true}
                      onUpdate={updateSubject}
                    />
                  </Col>
                </Row>
              </Card>
              <Row className="mx-0">
                {filteredNotes.length === 0 ? (
                  subject?.value === "0" || subject?.label === "View All" ? (
                    type === "ban_unban" ? (
                      <p className="mt-2">No Ban / Unban Note Records</p>
                    ) : type === "group" ? (
                      <p className="mt-2">No Group Meeting Note Records</p>
                    ) : type === "standard" ? (
                      <p className="mt-2">No Standard Note Records</p>
                    ) : type === "contact" ? (
                      <p className="mt-2">No Contact Note Records</p>
                    ) : type === "intervention" ? (
                      <p className="mt-2">No Intervention Note Records</p>
                    ) : type === "referral" ? (
                      <p className="mt-2">No Referral Note Records</p>
                    ) : type === "student" ? (
                      <p className="mt-2">No Student Connection Note Records</p>
                    ) : type === "infraction" ? (
                      <p className="mt-2">No Infraction Note Records</p>
                    ) : type === "intake" ? (
                      <p className="mt-2">No Intake Note Records</p>
                    ) : type === "exit" ? (
                      <p className="mt-2">No Exit Note Records</p>
                    ) : (
                      <p className="mt-2">No Note Records</p>
                    )
                  ) : type === "ban_unban" ? (
                    <p className="mt-2">
                      No Ban / Unban Note Records {subject?.label !== "Unassigned" ? `for ${subject?.label}` : " are Unassigned"}
                    </p>
                  ) : type === "group" ? (
                    <p className="mt-2">
                      No Group Meeting Note Records {subject?.label !== "Unassigned" ? `for ${subject?.label}` : " are Unassigned"}
                    </p>
                  ) : type === "standard" ? (
                    <p className="mt-2">
                      No Standard Note Records {subject?.label !== "Unassigned" ? `for ${subject?.label}` : " are Unassigned"}
                    </p>
                  ) : type === "contact" ? (
                    <p className="mt-2">
                      No Contact Note Records {subject?.label !== "Unassigned" ? `for ${subject?.label}` : " are Unassigned"}
                    </p>
                  ) : type === "intervention" ? (
                    <p className="mt-2">
                      No Intervention Note Records {subject?.label !== "Unassigned" ? `for ${subject?.label}` : " are Unassigned"}
                    </p>
                  ) : type === "referral" ? (
                    <p className="mt-2">
                      No Referral Note Records {subject?.label !== "Unassigned" ? `for ${subject?.label}` : " are Unassigned"}
                    </p>
                  ) : type === "student" ? (
                    <p className="mt-2">
                      No Student Connection Note Records {subject?.label !== "Unassigned" ? `for ${subject?.label}` : " are Unassigned"}
                    </p>
                  ) : type === "infraction" ? (
                    <p className="mt-2">
                      No Infraction Note Records {subject?.label !== "Unassigned" ? `for ${subject?.label}` : " are Unassigned"}
                    </p>
                  ) : type === "intake" ? (
                    <p className="mt-2">
                      No Intake Note Records {subject?.label !== "Unassigned" ? `for ${subject?.label}` : " are Unassigned"}
                    </p>
                  ) : type === "exit" ? (
                    <p className="mt-2">
                      No Exit Note Records {subject?.label !== "Unassigned" ? `for ${subject?.label}` : " are Unassigned"}
                    </p>
                  ) : (
                    <p className="mt-2">No Note Records {subject?.label !== "Unassigned" ? `for ${subject?.label}` : " are Unassigned"}</p>
                  )
                ) : (
                  filteredNotes
                    .slice(0, notesLoaded)
                    .map(
                      (note: Note) =>
                        (note.notetype === "ban_unban" && <BanView key={note.id} n={note} />) ||
                        (note.notetype === "group" && (
                          <GroupMeetingView key={note.id} n={note} downloadReport={downloadReport} setReportType={setReportType} />
                        )) ||
                        (note.notetype === "standard" && (
                          <StandardView
                            key={note.id}
                            n={note}
                            setEdit={updateEditNote}
                            setCurrent={setCurrentNote}
                            downloadReport={downloadReport}
                            setReportType={setReportType}
                          />
                        )) ||
                        (note.notetype === "contact" && (
                          <ContactView
                            key={note.id}
                            n={note}
                            setEdit={updateEditNote}
                            setCurrent={setCurrentNote}
                            downloadReport={downloadReport}
                            setReportType={setReportType}
                          />
                        )) ||
                        (note.notetype === "intervention" && (
                          <InterventionView
                            key={note.id}
                            n={note}
                            setEdit={updateEditNote}
                            setCurrent={setCurrentNote}
                            downloadReport={downloadReport}
                            setReportType={setReportType}
                          />
                        )) ||
                        (note.notetype === "referral" && (
                          <ReferralView
                            key={note.id}
                            n={note}
                            setEdit={updateEditNote}
                            setCurrent={setCurrentNote}
                            downloadReport={downloadReport}
                            setReportType={setReportType}
                          />
                        )) ||
                        (note.notetype === "student" && (
                          <StudentConnectionsView
                            key={note.id}
                            n={note}
                            setEdit={updateEditNote}
                            setCurrent={setCurrentNote}
                            downloadReport={downloadReport}
                            setReportType={setReportType}
                          />
                        )) ||
                        (note.notetype === "infraction" && (
                          <InfractionView
                            key={note.id}
                            n={note}
                            noteType={types.find((t: any) => t.key === "infraction")}
                            setCurrentNoteType={setCurrentNoteType}
                            setEdit={updateEditNote}
                            setCurrent={setCurrentNote}
                            buildPDF={buildPDF}
                            canvas={canvas}
                            setCanvas={setCanvas}
                          />
                        )) ||
                        (note.notetype === "intake" && (
                          <IntakeView
                            key={note.id}
                            n={note}
                            setEdit={updateEditNote}
                            setCurrent={setCurrentNote}
                            downloadReport={downloadReport}
                            setReportType={setReportType}
                          />
                        )) ||
                        (note.notetype === "exit" && (
                          <ExitView
                            key={note.id}
                            n={note}
                            setEdit={updateEditNote}
                            setCurrent={setCurrentNote}
                            downloadReport={downloadReport}
                            setReportType={setReportType}
                          />
                        )) || (
                          <CustomNoteView
                            key={note.id}
                            n={note}
                            setEdit={updateEditNote}
                            setCurrent={setCurrentNote}
                            type={types.find((t: any) => t.key === note.notetype)}
                          />
                        ),
                    )
                )}
              </Row>
            </>
          )}

          {createNote === true && (
            <>
              <Row>
                <Col>
                  <h4 className="mb-0 app-header-text">Create Note</h4>
                </Col>
                <Col className="text-end">
                  <Button className="app-primary-color button" onClick={() => cancelEditing()}>
                    Cancel
                  </Button>
                </Col>
              </Row>
              <Row>
                <CreateNote
                  fetchNotes={getNotes}
                  setViewNotes={setViewNotes}
                  setCreateNote={setCreateNote}
                  setShowToast={setShowToast}
                  setToastText={setToastText}
                  setToastBody={setToastBody}
                  activeFamily={activeFamily}
                  saveIsEnabled={saveIsEnabled}
                  setSaveIsEnabled={setSaveIsEnabled}
                  setUserToSave={setUserToSave}
                  setSummary={setSummary}
                />
                <SaveChangesModal showModal={showModal} setShowModal={setShowModal} onLeave={() => updateViewNotes(true)} />
              </Row>
            </>
          )}

          {editNote === true && (
            <>
              <Row>
                <Col>
                  <h4 className="mb-0 app-header-text">Edit Note</h4>
                </Col>
                <Col className="text-end">
                  <Button className="app-primary-color button" onClick={() => cancelEditing()}>
                    Back
                  </Button>
                </Col>
              </Row>
              <Row>
                <EditNote
                  fetchNotes={getNotes}
                  note={currentNote}
                  noteType={currentNoteType}
                  family={familyMembers}
                  activeFamily={activeFamily}
                  setViewNotes={setViewNotes}
                  setEditNote={setEditNote}
                  setShowToast={setShowToast}
                  setToastText={setToastText}
                  setToastBody={setToastBody}
                  saveIsEnabled={saveIsEnabled}
                  setSaveIsEnabled={setSaveIsEnabled}
                  setUserToSave={setUserToSave}
                  setSummary={setSummary}
                  myCase={myCase}
                  setIsLoading={setIsLoading}
                />
                <SaveChangesModal showModal={showModal} setShowModal={setShowModal} onLeave={() => updateViewNotes(true)} />
              </Row>
            </>
          )}
        </>
      )}
      <div style={{ minHeight: 90 }}>
        {loadingMore ? (
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        ) : (
          viewNotes === true &&
          notesLoaded < filteredNotes.length && (
            <Row className="justify-content-center">
              <Col xs="auto">
                <Button className="app-primary-color button mb-5" onClick={() => addMoreNotes()}>
                  Load More
                </Button>
              </Col>
            </Row>
          )
        )}
      </div>
    </>
  );
};

export default Notes;
